import {useMutation} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {toast} from '@ui/toast/toast';
import {UseFormReturn} from 'react-hook-form';
import {onFormQueryError} from '@common/errors/on-form-query-error';

interface AssignLicensePayload {
  id: number;
  username: string;
  verify?: boolean;
}

interface AssignLicenseResponse extends BackendResponse {
  data?: any;
}

export function useAssignLicense(form?: UseFormReturn<any>) {
  return useMutation({
    mutationFn: (payload: AssignLicensePayload) => assignLicense(payload),
    onSuccess: (response, variables) => {
      if (!variables.verify) {
        toast.positive('License assigned successfully');
        queryClient.invalidateQueries({queryKey: ['licenses']});
      }
    },
    onError: form ? r => onFormQueryError(r, form) : undefined,
  });
}

async function assignLicense(
  payload: AssignLicensePayload,
): Promise<AssignLicenseResponse> {
  // Generate a token first
  const tokenResponse = await apiClient.post('drive/generate-token', {
    endpoint_type: 'licenses',
  });

  const token = tokenResponse.data.token;

  // Call external API directly with axios
  const endpoint = payload.verify
    ? 'https://apcrosa.syscomlive.co.uk/api/v1/drive/license/try-assign-to-user'
    : 'https://apcrosa.syscomlive.co.uk/api/v1/drive/license/assign-to-user';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Token': token,
    },
    body: JSON.stringify({
      ...payload,
      use_for_drive: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Check for API error responses (status: 0 indicates error)
  if (data.status === 0) {
    throw new Error(data.msg || 'An error occurred while assigning the license');
  }

  return data;
}
